import { inject, Injectable } from '@angular/core';
import { CustomCart } from '@cart/const';
import { ActiveCartService } from '@cart/data-access';
import {
  CheckoutValidator,
  getBonusApplyingErrorMessage,
  isSuccessfulBonusApplying,
  ValidationErrorKeys,
  ValidationErrors,
} from '@checkout/const';
import { ApplyingBonusFacade } from '@checkout/data-access';
import { BonusSystemFeatureAvailableService } from '@user/feature';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

export const MIN_ALLOWED_AMOUNT = 1000;

@Injectable({
  providedIn: 'root',
})
export class ApplyingBonusService implements CheckoutValidator {
  private readonly isValidSubject = new BehaviorSubject<boolean>(true);
  readonly isValid$ = this.isValidSubject.asObservable();

  private readonly bonusSystemFeatureAvailableService = inject(BonusSystemFeatureAvailableService);
  private readonly applyingBonusFacade = inject(ApplyingBonusFacade);
  private readonly activeCartService = inject(ActiveCartService);

  setApplyingBonus(value: number): void {
    this.applyingBonusFacade.setApplyingBonus(value);
  }

  getApplyingBonus(): Observable<number | undefined> {
    return this.applyingBonusFacade.getApplyingBonus();
  }

  getApplyingResultCode(): Observable<string | undefined> {
    return this.activeCartService.getActive().pipe(
      map((cart: CustomCart) => cart?.bonus?.applyingResultCode),
      distinctUntilChanged(),
    );
  }

  validate(): Observable<ValidationErrors> {
    return combineLatest({
      formValue: this.getApplyingBonus(),
      useBonusLimit: this.bonusSystemFeatureAvailableService.useBonusLimit(),
      applyingResultCode: this.getApplyingResultCode(),
      isFeatureAvailable: this.bonusSystemFeatureAvailableService.isFeatureAvailableForUser(),
    }).pipe(
      map(({ formValue, useBonusLimit, applyingResultCode, isFeatureAvailable }) => {
        const errors: ValidationErrors = {};

        if (!isFeatureAvailable) {
          return errors;
        }

        if (useBonusLimit && formValue > 0 && formValue < MIN_ALLOWED_AMOUNT) {
          errors[ValidationErrorKeys.MINIMUM_BONUS_VALUE] = `Списать можно не менее ${MIN_ALLOWED_AMOUNT} баллов`;
        }

        if (!isSuccessfulBonusApplying(applyingResultCode)) {
          errors[ValidationErrorKeys.BONUS_APPLYING_FAILED] = getBonusApplyingErrorMessage(applyingResultCode);
        }

        return errors;
      }),
      tap((errors) => {
        this.isValidSubject.next(!errors[ValidationErrorKeys.MINIMUM_BONUS_VALUE]);
      }),
    );
  }
}
